import { useMemo } from 'react';
import { resolveRichText, verifyCrossLinks } from '@vibesites/runtime';
import {
  EditableToken,
  useContentToken,
  useVibesites,
} from '@vibesites/runtime/react';
import { transformBedroomRangeInResolvedHtml } from '../utils/bedroomFormatting';

interface HeroSubtitleRichTextProps {
  path: string;
  defaultValue?: string;
  className?: string;
}

function stripSingleWrappingParagraph(html: string): string {
  const match = html.match(/^<p>([\s\S]*)<\/p>$/);
  if (!match) return html;
  const inner = match[1];
  if (/<p[\s>]|<div[\s>]|<h[1-6][\s>]/i.test(inner)) return html;
  return inner;
}

export function HeroSubtitleRichText({
  path,
  defaultValue,
  className,
}: HeroSubtitleRichTextProps) {
  const { property, pagePaths } = useVibesites();
  const rawHtml = useContentToken(path, { defaultValue });

  const resolvedHtml = useMemo(() => {
    if (!rawHtml) return '';
    let html = resolveRichText(rawHtml, property as Record<string, unknown>);
    html = transformBedroomRangeInResolvedHtml(html, property);
    html = verifyCrossLinks(html, pagePaths);
    return stripSingleWrappingParagraph(html);
  }, [rawHtml, property, pagePaths]);

  // IMPORTANT: wrap as a <div>, not <h1>. The subtitle rich-text content is
  // user-editable and often contains an <h1>…</h1> (e.g. Sister Communities:
  // "Nearby Communities From the Price Brothers Family"). HTML's parser
  // auto-closes an open <h1> when it hits another <h1> inside, which empties
  // this wrapper and strips its className — the subtitle styles then land on
  // an empty element and the inner h1 renders unstyled, so the subtitle
  // appears blank. A <div> wrapper is parsed correctly around any heading
  // content, and the inner heading inherits font-size / color / family from
  // the className styles via Tailwind's `h1{font-size:inherit}` preflight.
  return (
    <EditableToken
      path={path}
      type="richtext"
      as="div"
      className={className}
      defaultValue={defaultValue}
    >
      <span
        data-vibesite-richtext=""
        dangerouslySetInnerHTML={{ __html: resolvedHtml }}
        style={{ display: 'contents' }}
      />
    </EditableToken>
  );
}
